import { useCallback, useMemo } from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"
import { programStartResetFor } from "@/types/programs"
import {
  getLevelLabel,
  getGoalLabel,
  getPlaceLabel,
  getGenderLabel,
  getProgramName,
  formatMemberSince,
  formatDateFR,
} from "@/data/labels"

/**
 * Âge exact au jour près depuis la date de naissance. `wizard_submissions.age` est figé
 * à l'inscription : au bout d'un an il ment. On préfère donc le recalcul quand la date de
 * naissance est connue, et on ne retombe sur la colonne que pour les comptes qui n'en ont pas.
 */
function computeAge(birthDate: string | null): number | null {
  if (!birthDate) return null
  const b = new Date(birthDate)
  if (isNaN(b.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - b.getFullYear()
  const m = now.getMonth() - b.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--
  return age >= 0 && age < 130 ? age : null
}

/**
 * Champs éditables du profil.
 * Le user_id, email, created_at ne sont pas éditables ici.
 */
export interface ProfileUpdate {
  first_name?: string
  gender?: "male" | "female"
  age?: number
  height?: number
  weight?: number
  weight_start?: number
  place?: "gym" | "home"
  level_id?: string
  goal_id?: string
  birth_date?: string | null
  avatar_url?: string | null
  training_days?: string[]
  target_weight?: number | null
  phone?: string | null
}

/**
 * Vue enrichie du profil : données brutes + champs dérivés prêts pour l'UI.
 */
export interface ProfileView {
  // Brut
  raw: {
    id: string
    user_id: string
    email: string
    first_name: string
    gender: "male" | "female"
    age: number
    height: number
    weight: number
    weight_start: number | null
    place: "gym" | "home"
    level_id: string
    goal_id: string
    /** NULL pour les comptes créés depuis le lot 3. */
    offer_id: string | null
    birth_date: string | null
    program_week_start: string | null
    avatar_url: string | null
    created_at: string
    training_days: string[]
    target_weight: number | null
    phone: string | null
    /** Accord blessures (lot 3). NULL = aucun accord enregistré. */
    injury_consent_at: string | null
  }

  // Affichage direct
  firstName: string
  email: string
  weight: number
  weightStart: number
  weightDiff: number // négatif = perdu
  height: number
  initials: string

  /** Âge en années, recalculé depuis la date de naissance quand elle est connue. */
  age: number | null

  // Labels
  genderLabel: string
  levelLabel: string
  goalLabel: string
  placeLabel: string
  birthDateFR: string
  memberSince: string

  // Programme — identité seulement.
  // L'avancement (semaine courante, séances faites, total) vit dans `useProgramBlock` :
  // il dépend du catalogue `programs` et de `session_feedback`, pas du profil, et le
  // mélanger ici avait produit les deux compteurs contradictoires de la carte du bloc.
  programName: string
}

export function useProfile(): {
  profile: ProfileView | null
  loading: boolean
  error: string | null
  updateProfile: (updates: ProfileUpdate) => Promise<{ error: string | null }>
  refresh: () => Promise<void>
} {
  const { wizardData, wizardLoading, refreshWizardData } = useAuth()

  const updateProfile = useCallback(
    async (updates: ProfileUpdate): Promise<{ error: string | null }> => {
      if (!wizardData?.user_id) {
        return { error: "Non authentifié" }
      }
      try {
        const { error } = await supabase
          .from("wizard_submissions")
          .update({ ...updates, ...programStartResetFor(wizardData, updates) })
          .eq("user_id", wizardData.user_id)

        if (error) {
          console.error("[useProfile] update error:", error)
          return { error: error.message }
        }

        // Rafraîchit les données dans le AuthContext
        await refreshWizardData()
        return { error: null }
      } catch (err) {
        console.error("[useProfile] update exception:", err)
        return {
          error: err instanceof Error ? err.message : "Erreur inconnue",
        }
      }
    },
    [wizardData?.user_id, refreshWizardData]
  )

  const profile: ProfileView | null = useMemo(() => {
    if (!wizardData) return null

    const raw = wizardData as ProfileView["raw"]
    const firstName =
      raw.first_name.charAt(0).toUpperCase() +
      raw.first_name.slice(1).toLowerCase()

    const weight = raw.weight ?? 0
    const weightStart = raw.weight_start ?? raw.weight ?? 0
    const weightDiff = weight - weightStart

    const initials = firstName.slice(0, 2).toUpperCase()

    return {
      raw,
      firstName,
      email: raw.email,
      weight,
      weightStart,
      weightDiff,
      height: raw.height ?? 0,
      initials,
      age: computeAge(raw.birth_date) ?? raw.age ?? null,
      genderLabel: getGenderLabel(raw.gender),
      levelLabel: getLevelLabel(raw.level_id),
      goalLabel: getGoalLabel(raw.goal_id),
      placeLabel: getPlaceLabel(raw.place),
      birthDateFR: formatDateFR(raw.birth_date),
      memberSince: formatMemberSince(raw.created_at),
      programName: getProgramName(raw.level_id, raw.goal_id),
    }
  }, [wizardData])

  return {
    profile,
    loading: wizardLoading,
    error: null,
    updateProfile,
    refresh: refreshWizardData,
  }
}
